import React, { useEffect, useState } from "react";
import {
  Award,
  BookOpen,
  ChevronLeft,
  Crosshair,
  Crown,
  Star,
  TrendingUp,
  Users,
} from "lucide-react";
import { useEvents } from "../hooks";
import { useRankings } from "../hooks";
import { useToast } from "../context/ToastContext";
import { predictionsApi } from "../api/predictions";
import type { RankingUser } from "../types";
import type { EventVoteInfo, VoteHistory } from "../types/swagger";
import { splitEventsByTimeline } from "../utils/eventSections";
import { DartsEvent } from "../types";
import { useMypageContext } from "../context/MypageContext";

interface PredictionVoteViewProps {
  onBack: () => void;
}

const SectionTitle: React.FC<{
  children: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ children, icon }) => (
  <div className="flex items-center gap-2 mb-3 pl-1">
    {icon && <span className="text-slate-400">{icon}</span>}
    <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
      {children}
    </h2>
  </div>
);

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "",
}) => (
  <div
    className={`bg-white border border-slate-100 rounded-2xl p-4 shadow-sm ${className}`}
  >
    {children}
  </div>
);

/** 年間ランキングイベントグループの判定（singlesPointFlag がランキング対象イベントの目印） */
const isRankingEvent = (event: DartsEvent): boolean => !!event.singlesPointFlag;

const PredictionVoteView: React.FC<PredictionVoteViewProps> = ({ onBack }) => {
  const { events, isLoading: eventsLoading } = useEvents();
  const { rankings, isLoading: rankingsLoading } = useRankings();
  const { showToast } = useToast();
  const { mypage } = useMypageContext();

  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasJustVoted, setHasJustVoted] = useState(false);
  const [eventVoteInfo, setEventVoteInfo] = useState<EventVoteInfo | null>(
    null,
  );
  const [voteHistories, setVoteHistories] = useState<VoteHistory[]>([]);
  const [isVoteDataLoading, setIsVoteDataLoading] = useState(true);

  // 年間ランキング対象の未来直近イベントを特定
  const { upNext, schedule, pastEvents } = splitEventsByTimeline(events);
  const allUpcoming = upNext ? [upNext, ...schedule] : schedule;
  const targetEvent = allUpcoming.find((e) => isRankingEvent(e)) ?? null;

  useEffect(() => {
    if (eventsLoading) return;
    const load = async () => {
      setIsVoteDataLoading(true);
      try {
        const [info, histories] = await Promise.all([
          targetEvent
            ? predictionsApi.getEventVoteInfo(targetEvent.id)
            : Promise.resolve(null),
          predictionsApi.getVoteHistories(),
        ]);
        setEventVoteInfo(info);
        setVoteHistories(histories);
      } catch {
        showToast("データの取得に失敗しました", "error");
      } finally {
        setIsVoteDataLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsLoading, targetEvent?.id, showToast]);

  const handleSelectParticipant = (userId: string) => {
    setSelectedUserId((prev) => (prev === userId ? "" : userId));
  };

  const handleSubmit = async () => {
    if (!targetEvent || !selectedUserId) return;
    const isUpdating = !!myVoteStatus;
    setIsSubmitting(true);
    try {
      await predictionsApi.createEventVote(targetEvent.id, {
        voted_user_id: selectedUserId,
      });
      showToast(
        isUpdating ? "優勝予想を変更しました" : "優勝予想を投票しました",
        "success",
      );
      setSelectedUserId("");
      setHasJustVoted(true);
      const [info, histories] = await Promise.all([
        predictionsApi.getEventVoteInfo(targetEvent.id),
        predictionsApi.getVoteHistories(),
      ]);
      setEventVoteInfo(info);
      setVoteHistories(histories);
    } catch {
      showToast("投票に失敗しました。もう一度お試しください。", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const top5Rankings = [...rankings]
    .sort((a, b) => a.rank - b.rank)
    .slice(0, 5);

  // 自分の投票状況（vote_statuses から自分を検索）
  const myVoteStatus =
    eventVoteInfo?.vote_statuses.find(
      (vs) => vs.voter.dartslive_card_name === mypage?.dartslive_card_name,
    ) ?? null;
  const isEventScheduled = targetEvent?.status === "scheduled";
  // イベント開始前は変更可能。開始後・終了後は固定表示。
  const showVotedFinalState =
    (hasJustVoted || !!myVoteStatus) && !isEventScheduled;

  // 投票変更モード: イベント開始前は投票済みの選択肢を事前選択
  useEffect(() => {
    if (myVoteStatus && targetEvent?.status === "scheduled") {
      setSelectedUserId((prev) => prev || myVoteStatus.voted.user_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myVoteStatus?.voted.user_id, targetEvent?.status]);

  // 投票履歴: 現在のイベント以外の過去投票
  const pastHistories = voteHistories.filter(
    (vh) => vh.event_id !== targetEvent?.id,
  );

  // 過去の大会結果: voteHistories を正とし、Soft Darts TL 2026 グループのみ表示
  const targetGroupId =
    targetEvent?.eventGroupId ?? pastEvents.find(isRankingEvent)?.eventGroupId;
  const pastGroupEventIdSet = new Set(
    targetGroupId
      ? pastEvents
          .filter((e) => e.eventGroupId === targetGroupId)
          .map((e) => e.id)
      : [],
  );
  const pastGroupResults = pastHistories.filter(
    (vh) =>
      pastGroupEventIdSet.has(vh.event_id) && !!vh.winner_dartslive_card_name,
  );

  // 選択中のユーザー名
  const selectedParticipantName =
    eventVoteInfo?.participants.find((p) => p.user_id === selectedUserId)
      ?.dartslive_card_name ?? "";

  const getRankStyle = (rank: number): string => {
    if (rank === 1) return "bg-yellow-50 text-yellow-600 border-yellow-200";
    if (rank === 2) return "bg-slate-100 text-slate-600 border-slate-200";
    if (rank === 3) return "bg-orange-50 text-orange-600 border-orange-200";
    return "bg-slate-50 text-slate-500 border-slate-100";
  };

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-300 overflow-hidden">
      {/* ヘッダー */}
      <header className="pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-3 px-4 flex items-center gap-3 sticky top-0 bg-[#f8fafc]/80 backdrop-blur-xl z-20 border-b border-slate-200/50 shrink-0">
        <button
          onClick={onBack}
          className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-100 active:scale-90 transition-all"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
        </button>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-gradient-to-tr from-amber-500 to-orange-400 flex items-center justify-center shadow-md rotate-3">
            <Crosshair
              className="w-[18px] h-[18px] text-white -rotate-3"
              strokeWidth={2.5}
            />
          </div>
          <h1 className="text-xl font-black tracking-tighter uppercase bg-gradient-to-r from-amber-500 to-orange-400 bg-clip-text text-transparent">
            優勝予想投票
          </h1>
        </div>
      </header>

      <div className="overflow-y-auto scrollbar-hide flex-1 px-5 py-5 pb-24 space-y-6 text-left">
        {/* --- 投票対象イベント --- */}
        <section>
          <SectionTitle icon={<Crosshair className="w-3.5 h-3.5" />}>
            投票対象イベント
          </SectionTitle>
          {eventsLoading ? (
            <Card>
              <p className="text-xs text-slate-400 font-bold text-center py-4">
                読み込み中...
              </p>
            </Card>
          ) : !targetEvent ? (
            <Card>
              <p className="text-sm text-slate-400 font-bold text-center py-4">
                現在投票できるイベントはありません
              </p>
            </Card>
          ) : (
            <Card>
              {/* イベント情報 */}
              <div className="mb-4 pb-3 border-b border-slate-100">
                {targetEvent.tournament && (
                  <span className="text-[10px] font-black uppercase tracking-widest text-amber-500">
                    {targetEvent.tournament}
                  </span>
                )}
                <p className="font-black text-slate-800 text-base mt-0.5">
                  {targetEvent.title || targetEvent.tournament}
                </p>
                <p className="text-[11px] text-slate-400 font-bold mt-0.5">
                  {targetEvent.date}
                </p>
              </div>

              {/* イベント開始・終了後かつ投票済みの場合は Good luck! を表示 */}
              {showVotedFinalState ? (
                <div className="flex flex-col items-center justify-center py-6 gap-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-amber-400 to-orange-400 flex items-center justify-center shadow-lg shadow-amber-200">
                    <Star
                      className="w-7 h-7 text-white"
                      fill="white"
                      strokeWidth={0}
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-black text-slate-800 tracking-tight">
                      Good luck!
                    </p>
                    <p className="text-[11px] text-slate-400 font-bold mt-1">
                      {myVoteStatus && (
                        <>
                          <span className="text-amber-600 font-black">
                            {myVoteStatus.voted.dartslive_card_name}
                          </span>{" "}
                          に投票済みです
                        </>
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* メンバー一覧ラジオ選択 */}
                  {!!myVoteStatus && isEventScheduled && (
                    <p className="text-[11px] text-amber-500 font-bold mb-3 text-center">
                      ※ イベント開始前は投票を変更できます
                    </p>
                  )}
                  {isVoteDataLoading ? (
                    <p className="text-xs text-slate-400 py-2">読み込み中...</p>
                  ) : !eventVoteInfo ||
                    eventVoteInfo.participants.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2">
                      参加者情報がありません
                    </p>
                  ) : (
                    <div className="space-y-2 mb-5">
                      {eventVoteInfo.participants.map((participant) => {
                        const isSelected =
                          selectedUserId === participant.user_id;
                        return (
                          <button
                            key={participant.user_id}
                            onClick={() =>
                              handleSelectParticipant(participant.user_id)
                            }
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all active:scale-[0.98] text-left ${
                              isSelected
                                ? "bg-amber-50 border-amber-300 shadow-sm shadow-amber-100"
                                : "bg-slate-50 border-slate-100 hover:border-slate-200"
                            }`}
                          >
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                                isSelected
                                  ? "border-amber-500 bg-amber-500"
                                  : "border-slate-300"
                              }`}
                            >
                              {isSelected && (
                                <Star
                                  className="w-2.5 h-2.5 text-white"
                                  fill="white"
                                  strokeWidth={0}
                                />
                              )}
                            </div>
                            <span
                              className={`text-sm font-bold transition-colors ${
                                isSelected ? "text-amber-700" : "text-slate-600"
                              }`}
                            >
                              {participant.dartslive_card_name}
                            </span>
                            {isSelected && (
                              <span className="ml-auto text-[10px] font-black text-amber-500 uppercase tracking-wider">
                                予想
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* 投票ボタン */}
                  <button
                    onClick={handleSubmit}
                    disabled={
                      isSubmitting ||
                      !selectedUserId ||
                      !eventVoteInfo ||
                      eventVoteInfo.participants.length === 0
                    }
                    className="w-full py-3 rounded-xl font-black text-sm uppercase tracking-wider transition-all bg-amber-500 text-white shadow-md shadow-amber-500/30 hover:bg-amber-600 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"
                  >
                    <Crosshair className="w-4 h-4" strokeWidth={2.5} />
                    {isSubmitting
                      ? "投票中..."
                      : !selectedUserId
                        ? "優勝予想を選んでください"
                        : myVoteStatus
                          ? `${selectedParticipantName} に変更する`
                          : `${selectedParticipantName} に投票する`}
                  </button>
                </>
              )}
            </Card>
          )}
        </section>

        {/* --- 投票状況 --- */}
        <section>
          <SectionTitle icon={<Users className="w-3.5 h-3.5" />}>
            投票状況
          </SectionTitle>
          {isVoteDataLoading ? (
            <Card>
              <p className="text-xs text-slate-400 font-bold text-center py-4">
                読み込み中...
              </p>
            </Card>
          ) : !targetEvent ? null : (eventVoteInfo?.vote_statuses.length ??
              0) === 0 ? (
            <Card>
              <p className="text-sm text-slate-400 font-bold text-center py-4">
                まだ投票はありません
              </p>
            </Card>
          ) : (
            <Card className="divide-y divide-slate-50">
              {eventVoteInfo!.vote_statuses.map((vs) => (
                <div
                  key={`${vs.voter.user_id}-${vs.voted.user_id}`}
                  className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                >
                  <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                    <Star
                      className="w-3.5 h-3.5 text-amber-500"
                      strokeWidth={2}
                    />
                  </div>
                  <p className="flex-1 text-[13px] font-black text-slate-700 min-w-0 truncate">
                    {vs.voter.dartslive_card_name}
                  </p>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-[10px] text-slate-400 font-bold">
                      予想
                    </span>
                    <span className="text-[12px] font-black text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                      {vs.voted.dartslive_card_name}
                    </span>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </section>

        {/* --- 投票履歴 --- */}
        <section>
          <SectionTitle icon={<BookOpen className="w-3.5 h-3.5" />}>
            投票履歴
          </SectionTitle>
          {isVoteDataLoading ? (
            <Card>
              <p className="text-xs text-slate-400 font-bold text-center py-4">
                読み込み中...
              </p>
            </Card>
          ) : pastHistories.length === 0 ? (
            <Card>
              <p className="text-sm text-slate-400 font-bold text-center py-4">
                過去の投票履歴はありません
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {pastHistories.map((vh) => (
                <Card key={vh.event_id}>
                  {/* イベントヘッダー */}
                  <div className="flex items-start justify-between gap-2 mb-3 pb-2.5 border-b border-slate-100">
                    <div className="min-w-0">
                      <p className="text-[13px] font-black text-slate-700 leading-tight">
                        {vh.event_name}
                      </p>
                    </div>
                    {vh.winner_dartslive_card_name && (
                      <div className="flex items-center gap-1 shrink-0 bg-yellow-50 border border-yellow-200 rounded-full px-2.5 py-1">
                        <Crown className="w-3 h-3 text-yellow-500 shrink-0" />
                        <span className="text-[11px] font-black text-yellow-700">
                          {vh.winner_dartslive_card_name}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* 自分の投票 */}
                  <div className="flex items-center gap-2">
                    <p className="flex-1 text-[12px] font-bold text-slate-600 min-w-0 truncate">
                      {vh.voter_dartslive_card_name}
                    </p>
                    <span className="text-[10px] text-slate-300 font-bold">
                      →
                    </span>
                    <span
                      className={`text-[11px] font-black px-2 py-0.5 rounded-full border shrink-0 ${
                        vh.is_correct
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-50 text-slate-500 border-slate-200"
                      }`}
                    >
                      {vh.voted_dartslive_card_name}
                    </span>
                    {vh.is_correct && (
                      <span className="text-[9px] font-black text-emerald-500 uppercase tracking-wider shrink-0">
                        ✓ 的中
                      </span>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* --- 参考情報セパレーター --- */}
        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-[#f8fafc] px-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.15em]">
              参考情報
            </span>
          </div>
        </div>

        {/* --- 年間ランキング TOP5 --- */}
        <section>
          <SectionTitle icon={<TrendingUp className="w-3.5 h-3.5" />}>
            年間ランキング TOP5
          </SectionTitle>
          {rankingsLoading ? (
            <Card className="bg-slate-50/60 border-slate-200/60">
              <p className="text-xs text-slate-400 font-bold text-center py-4">
                読み込み中...
              </p>
            </Card>
          ) : top5Rankings.length === 0 ? (
            <Card className="bg-slate-50/60 border-slate-200/60">
              <p className="text-sm text-slate-400 font-bold text-center py-4">
                ランキングデータがありません
              </p>
            </Card>
          ) : (
            <div className="bg-slate-50/60 border border-slate-200/60 rounded-2xl p-4 shadow-sm divide-y divide-slate-100">
              {top5Rankings.map((user: RankingUser) => (
                <div
                  key={user.name}
                  className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm border shrink-0 ${getRankStyle(user.rank)}`}
                  >
                    {user.rank === 1 ? (
                      <Crown className="w-4 h-4" />
                    ) : (
                      user.rank
                    )}
                  </div>
                  <p
                    className={`flex-1 font-bold text-[14px] truncate ${user.isMe ? "text-blue-700" : "text-slate-600"}`}
                  >
                    {user.name}
                  </p>
                  <div className="text-right shrink-0">
                    <span className="text-[13px] font-black text-slate-600">
                      {user.points}
                    </span>
                    <span className="text-[10px] text-slate-400 font-bold ml-0.5">
                      pt
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* --- 過去の大会結果 --- */}
        <section>
          <SectionTitle icon={<Award className="w-3.5 h-3.5" />}>
            過去の大会結果
          </SectionTitle>
          {isVoteDataLoading ? (
            <div className="bg-slate-50/60 border border-slate-200/60 rounded-2xl p-4 shadow-sm">
              <p className="text-xs text-slate-400 font-bold text-center py-4">
                読み込み中...
              </p>
            </div>
          ) : pastGroupResults.length === 0 ? (
            <div className="bg-slate-50/60 border border-slate-200/60 rounded-2xl p-4 shadow-sm">
              <p className="text-sm text-slate-400 font-bold text-center py-4">
                過去の大会結果はありません
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {pastGroupResults.map((vh) => (
                <div
                  key={vh.event_id}
                  className="bg-slate-50/60 border border-slate-200/60 rounded-2xl px-4 py-3 shadow-sm"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[13px] font-black text-slate-600 leading-tight truncate">
                        {vh.event_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Crown className="w-3 h-3 text-yellow-500 shrink-0" />
                      <span className="text-[12px] font-black text-slate-600">
                        {vh.winner_dartslive_card_name}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default PredictionVoteView;
